"use strict";

const _ = require("lodash");
const GridCell = require("../cell/GridCellComponent");
const EmptyColumnRendererComponent = require("../column_renderer/empty/EmptyColumnComponent");

class GridRowRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = this._createRootElement();
        this._rootElem.component = this._model;
        this._rootElem.tabIndex = -1;
        this._gridIsGrouped = _.get(this.model.getGrid(), "_groupBy", false);

        this._cells = {};
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _createRootElement() {
        return document.createElement("div");
    }

    _registerListeners() {
        this._model.addListener("ID", (this._onId = this._onId.bind(this)));
        this._model.addListener("Entity", (this._onEntity = this._onEntity.bind(this)));
        this._model.addListener(
            "UpdateEntityProperties",
            (this._onUpdateEntityProperties = this._onUpdateEntityProperties.bind(this))
        );
        this._model.addListener(
            "ColumnOrder",
            (this._onColumnOrder = this._onColumnOrder.bind(this))
        );
        this._model.addListener(
            "LockedColumns",
            (this._onLockedColumns = this._onLockedColumns.bind(this))
        );
        this._model.addListener(
            "ColumnComponents",
            (this._onColumnComponents = this._onColumnComponents.bind(this))
        );
        this._model.addListener("Selected", (this._onSelected = this._onSelected.bind(this)));
        this._model.addListener("Top", (this._onTop = this._onTop.bind(this)));
        this._model.addListener("Even", (this._onEven = this._onEven.bind(this)));
        this._model.addListener("Active", (this._onActive = this._onActive.bind(this)));
        this._model.addListener(
            "ColumnWidth",
            (this._onColumnWidth = this._onColumnWidth.bind(this))
        );
        this._rootElem.addEventListener("click", (this._onRowClick = this._onRowClick.bind(this)));
        this._rootElem.addEventListener(
            "dblclick",
            (this._onRowDoubleClick = this._onRowDoubleClick.bind(this))
        );
        this._rootElem.addEventListener(
            "mouseenter",
            (this._onMouseEnter = this._onMouseEnter.bind(this))
        );
        this._rootElem.addEventListener(
            "mouseleave",
            (this._onMouseLeave = this._onMouseLeave.bind(this))
        );
        this._rootElem.addEventListener(
            "mousedown",
            (this._onRowMouseDown = this._onRowMouseDown.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeAllListeners("ID");
        this._model.removeAllListeners("Entity");
        this._model.removeAllListeners("UpdateEntityProperties");
        this._model.removeAllListeners("ColumnOrder");
        this._model.removeAllListeners("LockedColumns");
        this._model.removeAllListeners("ColumnComponents");
        this._model.removeAllListeners("Selected");
        this._model.removeAllListeners("Top");
        this._model.removeAllListeners("Even");
        this._model.removeAllListeners("Active");
        this._model.removeAllListeners("ColumnWidth");
        this._rootElem.removeEventListener("click", this._onRowClick);
        this._rootElem.removeEventListener("dblclick", this._onRowDoubleClick);
        this._rootElem.removeEventListener("mouseenter", this._onMouseEnter);
        this._rootElem.removeEventListener("mouseleave", this._onMouseLeave);
        this._rootElem.removeEventListener("mousedown", this._onRowMouseDown);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._setId(this._model.getId());
            this._setEntity(this._model.getEntity());
            this._setTop(this._model.getTop());
            this._setEven(this._model.getEven());
            this._setDraggable(this._model._allowDragNDrop);
            resolve();
        });
        return prom;
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    shutdown() {
        let promiseArray = [];

        this._unregisterListeners();

        for (let cell in this._cells) {
            if (this._cells.hasOwnProperty(cell)) {
                this._cells[cell].removeAllListeners("CellAction");
                promiseArray.push(this._cells[cell].shutdown());
            }
        }

        return Promise.all(promiseArray).then(() => {
            this._model = undefined;
            this._rootElem.remove();
            this._textField = undefined;
            this._rootElem = undefined;
            this._cells = undefined;
        });
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /**
     * Fired when the unique key for the row has changed
     * @param newVal {string}
     * @private
     */
    _onId(newVal) {
        this._setId(newVal);
    }

    /**
     * Fired when the entity has changed
     * @param newVal {object}
     * @private
     */
    _onEntity(newVal) {
        this._setEntity(newVal);
    }

    /**
     * Fired when the entity has been updated
     * @param newProperties {object}
     * @private
     */
    _onUpdateEntityProperties(newProperties) {
        this._updateEntityProperties(newProperties);
    }

    /**
     * Fired when the grid column order has changed
     * @param newVal {array}
     * @private
     */
    _onColumnOrder(newVal) {
        this._setColumnOrder(newVal);
    }

    /**
     * Fired when the locked columns in the grid have changed
     * @param lockedColumns {Array} - Array of column ids that have been locked
     * @private
     */
    _onLockedColumns(newVal) {
        this._setLockedColumns(newVal);
    }

    /**
     * Fired when the grid columns have changed
     * @param newVal {object}
     * @private
     */
    _onColumnComponents(newVal) {
        this._setColumns(newVal);
    }

    /**
     * Fired when a row has been selected
     * @param newVal {boolean}
     * @private
     */
    _onSelected(newVal) {
        this._setSelected(newVal);
    }

    /**
     * Fired when a top value of the row has changed
     * @param newVal {Number} - The new top value in pixels
     * @private
     */
    _onTop(newVal) {
        this._setTop(newVal);
    }

    /**
     * Fired when the active state has been toggled
     * @param newVal {boolean}
     * @private
     */
    _onActive(newVal) {
        this._setActive(newVal);
    }

    /**
     * Fired when the even state has been toggled
     * @param newVal {boolean}
     * @private
     */
    _onEven(newVal) {
        this._setEven(newVal);
    }

    /**
     * Triggered by the 'UserResized' event for each column.  See model for event binding note.
     * @param columnData.column {String} - The id of the column that has been resized
     * @param columnData.width {Number} - The width in pixels of the column
     * @param columnData.event {Event} - The mouse event that triggered the resize
     * @private
     */
    _onColumnWidth(columnData) {
        let columnKey = columnData.column,
            columnWidth = columnData.width;
        if (!_.isUndefined(this._cells[columnKey])) {
            this._cells[columnKey].setWidth(columnWidth);
        }
    }

    /**
     * Fired when a row has been selected by the user
     * @param event {event}
     * @private
     */
    _onRowClick(event) {
        this._model._triggerRowUserSelected(event);
    }

    /**
     * Fired when a row has been clicked by the user
     * @param event {event}
     * @private
     */
    _onRowMouseDown(event) {
        // Prevents default text selection to happen when user double-clicks
        if (event.detail > 1) {
            event.preventDefault();
        }
    }

    /**
     * Fired when a row has been double-clicked by the user
     * @param event {Event}
     * @private
     */
    _onRowDoubleClick(event) {
        this._model._triggerRowUserDoubleClicked(event);
        window.getSelection().removeAllRanges();
    }

    /**
     * Fired when the user has entered the row with the mouse
     * @param event {event}
     * @private
     */
    _onMouseEnter(event) {
        this._model.setActive(true);
        this._model._triggerRowUserActive(event);
    }

    /**
     * Fired when the user has left the row with the mouse
     * @param event {event}
     * @private
     */
    _onMouseLeave(event) {
        this._model.setActive(false);
        this._model._triggerRowUserActive(event);
    }

    /**
     * Catch any actions emitted from the inner cells
     * @param eventData {object}
     * @private
     */
    _onCellAction(eventData) {
        this._model._triggerRowAction(eventData);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    /**
     * returns columns corresponding to current instance: locked or unlocked
     * @private
     */
    _getColumns() {
        return [];
    }

    /**
     * Set the row id in the DOM somewhere?
     * @param newVal {string}
     * @private
     */
    _setId(newVal) {
        if (newVal !== undefined) {
            this._rootElem.dataset.id = newVal;
        } else {
            this._rootElem.removeAttribute("data-id");
        }
    }

    /**
     * Build the row display from the entity
     * @param entity {object} New entity that contains data for the current row
     * @private
     */
    _setEntity(entity) {
        const hasGetter = (key) => {
            return typeof entity["get" + _.upperFirst(key)] === "function";
        };
        const getValue = (key) => {
            if (hasGetter(key)) {
                const val = entity.getProperty(key, true);
                return val !== undefined ? val : entity[key];
            }
            return undefined;
        };
        for (let cell in this._cells) {
            if (this._cells.hasOwnProperty(cell)) {
                this._cells[cell].removeAllListeners("CellAction");
                this._cells[cell].shutdown();
            }
        }
        this._cells = {};
        let columns = this._getColumns(),
            columnComponents = this._model.getColumnComponents();

        columns.forEach((columnKey) => {
            const colComponent = columnComponents[columnKey];
            let cellPath = colComponent ? colComponent.getPath() : undefined;

            let value;
            if (cellPath && cellPath.length > 0) {
                const pathParts = cellPath.split(".");
                const rootKey = pathParts[0];
                const subPath = pathParts.slice(1);
                const rootValue = getValue(rootKey);
                value = subPath.length > 0 ? _.get(rootValue, subPath) : rootValue;
            } else {
                value = getValue(columnKey);
            }

            let cell = new GridCell({
                id: columnKey,
                column: colComponent,
                columnRendererComponent: this._getColumnCellRenderer(colComponent),
                entityColumn: colComponent ? colComponent.getEntityColumn() : undefined,
                entity: entity,
                context: colComponent ? colComponent.getContext() : undefined,
                /* jshint -W014 */
                value: value,
                path: cellPath,
                shown: colComponent ? colComponent.getShown() : undefined,
            });

            cell.addListener("CellAction", this._onCellAction.bind(this));
            this._rootElem.appendChild(cell.renderer.rootElement);
            this._cells[columnKey] = cell;
        });
    }

    /**
     * Update any properties at the cell level that have changed
     * @param newProperties {object}
     * @private
     */
    _updateEntityProperties(newProperties) {
        let fullUpdate =
            Object.keys(newProperties).length > 1 ||
            (!newProperties.updatedOn && !newProperties.updatedBy);

        for (let cell in this._cells) {
            let cellComponent = this._cells[cell],
                cellId = cellComponent.getId(),
                cellPath = cellComponent.getPath(),
                oldValue = cellComponent.getValue(),
                /* jshint -W014 */
                newValue = cellPath
                    ? _.get(newProperties, cellPath, newProperties[cellId])
                    : _.get(newProperties, cellId);

            let allowUnset =
                fullUpdate &&
                cellId !== "updatedOn" &&
                cellId !== "updatedBy" &&
                cellId !== "createdBy" &&
                cellId !== "createdOn";
            if ((allowUnset || !_.isUndefined(newValue)) && !_.isEqual(oldValue, newValue)) {
                cellComponent.setValue(_.isUndefined(newValue) ? null : newValue);
            }
        }
    }

    /**
     * Rebuild the row with the new column order
     * @param columnOrder {Array} - Array of Strings (column ids) representing the order of the columns
     * @private
     */
    _setColumnOrder(columnOrder) {
        // console.info('SET LOCKED COLUMN ORDER');
        let instanceColumns = this._getColumns();
        let instanceColumnsOrder = _.intersection(columnOrder, instanceColumns);
        let columnComponents = this._model.getColumnComponents();
        let entity = this._model.getEntity();

        const hasGetter = (key) => {
            return typeof entity["get" + _.upperFirst(key)] === "function";
        };
        const getValue = (key) => {
            if (hasGetter(key)) {
                const val = entity.getProperty(key, true);
                return val !== undefined ? val : entity[key];
            }
            return undefined;
        };

        this._rootElem.innerHTML = "";
        instanceColumnsOrder.forEach((columnKey) => {
            if (_.isUndefined(this._cells[columnKey])) {
                const colComponent = columnComponents[columnKey];
                let cellPath = colComponent.getPath();

                let value;
                if (cellPath && cellPath.length > 0) {
                    const pathParts = cellPath.split(".");
                    const rootKey = pathParts[0];
                    const subPath = pathParts.slice(1);
                    const rootValue = getValue(rootKey);
                    value = subPath.length > 0 ? _.get(rootValue, subPath) : rootValue;
                } else {
                    value = getValue(columnKey);
                }

                let cell = new GridCell({
                    id: columnKey,
                    column: colComponent,
                    columnRendererComponent: this._getColumnCellRenderer(colComponent),
                    entityColumn: colComponent.getEntityColumn(),
                    entity: entity,
                    context: colComponent.getContext(),
                    path: cellPath,
                    /* jshint -W014 */
                    value: value,
                    shown: colComponent.getShown(),
                });

                cell.addListener("CellAction", this._onCellAction.bind(this));
                this._cells[columnKey] = cell;
            }
            this._rootElem.appendChild(this._cells[columnKey].renderer.rootElement);
        });
    }

    /**
     * Rebuild the row with the new column order
     * @param lockedColumns {Array} - Array of Strings (column ids) representing the order of the locked columns
     * @private
     */
    _setLockedColumns(/*lockedColumns*/) {
        const columns = this._getColumns();
        let columnComponents = this._model.getColumnComponents(),
            cellKeys = Object.keys(this._cells),
            cellsToRemove = _.difference(cellKeys, columns),
            cellsToAdd = _.difference(columns, cellKeys),
            entity = this._model.getEntity();
        const hasGetter = (key) => {
            return typeof entity["get" + _.upperFirst(key)] === "function";
        };
        const getValue = (key) => {
            if (hasGetter(key)) {
                const val = entity.getProperty(key, true);
                return val !== undefined ? val : entity[key];
            }
            return undefined;
        };
        cellsToRemove.forEach((columnKey) => {
            this._cells[columnKey].removeAllListeners("CellAction");
            this._cells[columnKey].shutdown();
            delete this._cells[columnKey];
        });
        cellsToAdd.forEach((columnKey) => {
            let nextCellIndex = columns.indexOf(columnKey) + 1,
                nextCellKey =
                    nextCellIndex <= columns.length - 1 ? columns[nextCellIndex] : undefined,
                nextCellRenderer =
                    /* jshint -W014 */
                    !_.isUndefined(nextCellKey) && this._cells[nextCellKey]
                        ? this._cells[nextCellKey].renderer.rootElement
                        : null,
                cellPath = columnComponents[columnKey].getPath();

            let value;
            if (cellPath && cellPath.length > 0) {
                const pathParts = cellPath.split(".");
                const rootKey = pathParts[0];
                const subPath = pathParts.slice(1);
                const rootValue = getValue(rootKey);
                value = subPath.length > 0 ? _.get(rootValue, subPath) : rootValue;
            } else {
                value = getValue(columnKey);
            }

            const colComponent = columnComponents[columnKey];
            let cell = new GridCell({
                id: columnKey,
                column: columnComponents[columnKey],
                columnRendererComponent: this._getColumnCellRenderer(colComponent),
                entityColumn: columnComponents[columnKey].getEntityColumn(),
                entity: entity,
                context: columnComponents[columnKey].getContext(),
                path: cellPath,
                /* jshint -W014 */
                value: value,
                shown: columnComponents[columnKey].getShown(),
            });

            cell.addListener("CellAction", this._onCellAction.bind(this));
            this._cells[columnKey] = cell;
            this._rootElem.insertBefore(cell.renderer.rootElement, nextCellRenderer);
        });
    }

    /**
     * Rebuild the row with the new columnComponents.
     * Only occurs if new columns are set on the existing row.
     * @param newVal {object} - Keyed object of columnComponents
     * @private
     */
    _setColumns(/*newVal*/) {
        // TODO: update column components and etc. here
        this._setEntity(this._model.getEntity());
    }

    /**
     * returns column renderer for the cell
     * @param colComponent
     * @return {*|EmptyColumnComponent}
     * @private
     */
    _getColumnCellRenderer(colComponent) {
        const isGrouped = this._gridIsGrouped;
        if (!colComponent) {
            return EmptyColumnRendererComponent;
        }

        if (colComponent.isGroupOnlyColumn || (colComponent.isCombinedColumn && isGrouped)) {
            //render empty cell is GroupOnlyColumn (there is separate row renderer for group rows)
            //and render empty cell if grouping is active and column is Combined column.
            return EmptyColumnRendererComponent;
        }

        return colComponent.getColumnRendererComponent();
    }
    /**
     * Select/Deselect the row
     * @param newVal {boolean}
     * @private
     */
    _setSelected(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("selected");
        } else {
            this._rootElem.classList.remove("selected");
        }
    }

    /**
     * Set the top value of the row
     * @param newVal {Number} - The new top value in pixels
     * @private
     */
    _setTop(newVal) {
        this._rootElem.style.top = newVal / 10 + "rem";
    }

    /**
     * Toggle the active display
     * @param newVal {boolean}
     * @private
     */
    _setActive(newVal) {
        if (newVal === true) {
            this._rootElem.classList.add("active");
        } else {
            this._rootElem.classList.remove("active");
        }
    }

    /**
     * Set the top value
     * @param newVal {Number} - The new top value in pixels
     * @private
     */
    _setEven(isEven) {
        if (isEven) {
            this._rootElem.classList.add("even");
        } else {
            this._rootElem.classList.remove("even");
        }
    }

    _setDraggable(isDraggable) {
        this._rootElem.setAttribute("draggable", isDraggable);
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = GridRowRenderer;
